"use client";

import { useEffect, useRef, useState } from "react";

const FLOATING_ACTION_RESERVED_BOTTOM_PX = 96;
const LOW_THRESHOLD = 0.2;
const HIGH_THRESHOLD = 0.6;

interface MorphTargetVisibilityState {
  readonly isTargetVisible: boolean;
  readonly shouldRenderFloatingTarget: boolean;
}

const initialVisibilityState: MorphTargetVisibilityState = {
  isTargetVisible: true,
  shouldRenderFloatingTarget: false,
};

export function useMorphTargetVisibility<ElementType extends Element>() {
  const targetRef = useRef<ElementType | null>(null);
  const [visibilityState, setVisibilityState] = useState(initialVisibilityState);

  useEffect(() => {
    const target = targetRef.current;

    if (!target) {
      return;
    }

    const observedTarget = target;
    let animationFrameId: number | null = null;

    function evaluateVisibility() {
      animationFrameId = null;

      setVisibilityState((prev) => {
        const next = resolveVisibilityState(observedTarget, prev);

        if (
          next.isTargetVisible === prev.isTargetVisible &&
          next.shouldRenderFloatingTarget === prev.shouldRenderFloatingTarget
        ) {
          return prev;
        }

        return next;
      });
    }

    function scheduleVisibilityEvaluation() {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(evaluateVisibility);
    }

    evaluateVisibility();
    window.addEventListener("scroll", scheduleVisibilityEvaluation, { passive: true });
    window.addEventListener("resize", scheduleVisibilityEvaluation);
    document.addEventListener("scroll", scheduleVisibilityEvaluation, {
      capture: true,
      passive: true,
    });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener("scroll", scheduleVisibilityEvaluation);
      window.removeEventListener("resize", scheduleVisibilityEvaluation);
      document.removeEventListener("scroll", scheduleVisibilityEvaluation, { capture: true });
    };
  }, []);

  return { ...visibilityState, targetRef };
}

function resolveVisibilityState(
  target: Element,
  currentState: MorphTargetVisibilityState,
): MorphTargetVisibilityState {
  const rect = target.getBoundingClientRect();
  const viewportTop = 0;
  const viewportBottom = window.innerHeight - FLOATING_ACTION_RESERVED_BOTTOM_PX;
  const visibleHeight = Math.max(
    0,
    Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop),
  );
  const ratio = rect.height > 0 ? visibleHeight / rect.height : 0;

  if (ratio >= HIGH_THRESHOLD) {
    return {
      isTargetVisible: true,
      shouldRenderFloatingTarget: false,
    };
  }

  if (ratio <= LOW_THRESHOLD) {
    return {
      isTargetVisible: false,
      shouldRenderFloatingTarget: rect.top < viewportTop,
    };
  }

  return currentState;
}
